<template>
  <Header v-if="shouldShowContent" />
  <router-view v-if="shouldShowContent" />
  <LeavePageConfirmation v-if="saveProtectState.showConfirm && shouldShowContent" />
  <Loading v-if="!shouldShowContent && !serverHasCrashed" />
  <Error v-if="serverHasCrashed" />
</template>

<script>
import Header from './components/Header.vue';
import useCurrentUser from './composables/useCurrentUser';
import LeavePageConfirmation from './components/LeavePageConfirmation.vue';
import useSaveProtect from './composables/useSaveProtect';
import useUsersQuery from './composables/useUsersQuery';
import usePicklistItemsQuery from '@/composables/usePicklistItemsQuery';
import { socket, initSocket } from '@/socket';
import { onBeforeUnmount, watch } from 'vue';
import Loading from './views/fallbacks/Loading.vue';
import Error from './views/fallbacks/Error.vue';
import { auth0 } from './composables/useAuth0Client';
import useCompaniesQuery from './composables/useCompaniesQuery';
import useContactsQuery from './composables/useContactsQuery';
import useServerCrash from './composables/useServerCrash';

export default {
  name: 'App',
  components: {
    Header,
    LeavePageConfirmation,
    Loading,
    Error,
  },
  setup() {
    const { state: saveProtectState } = useSaveProtect();
    const { isLoading, isAuthenticated, loginWithRedirect } = auth0;
    const { getCurrentUser, user } = useCurrentUser();
    const { getUsers, allUsers } = useUsersQuery();
    const { serverHasCrashed } = useServerCrash();

    onBeforeUnmount(() => {
      socket.disconnect();
    });

    async function fetchData() {
      initSocket();
      getCurrentUser();
      getUsers();
      useCompaniesQuery.useCompaniesQuery();
      useContactsQuery.useContactsQuery();
      usePicklistItemsQuery.usePicklistItemsQuery();
    }

    function login() {
      loginWithRedirect();
    }

    function checkAuthStatus() {
      if (!isAuthenticated.value && !isLoading.value) {
        login();
      }

      if (isAuthenticated.value && !isLoading.value) {
        fetchData();
      }
    }

    watch(isAuthenticated, checkAuthStatus);
    watch(isLoading, checkAuthStatus);
    checkAuthStatus();

    return {
      saveProtectState,
      isLoading,
      isAuthenticated,
      user,
      allUsers,
      serverHasCrashed,
    };
  },
  data() {
    return {
      shouldShowLoading: true,
    };
  },
  computed: {
    shouldShowContent() {
      return !this.isLoading && this.isAuthenticated && this.user.userid;
    },
  },
  watch: {
    isLoading: {
      deep: true,
      handler() {
        this.handleStartPage();
      },
    },
    isAuthenticated: {
      deep: true,
      handler() {
        this.handleStartPage();
      },
    },
    user: {
      deep: true,
      handler() {
        this.handleStartPage();
      },
    },
    allUsers: {
      deep: true,
      handler() {
        this.handleStartPage();
      },
    },
  },
  methods: {
    handleStartPage() {
      if (!this.allUsers?.length || !this.user?.userid || this.isLoading || !this.isAuthenticated) {
        return;
      }

      this.shouldShowLoading = false;
      if (this.$route.path !== '/') return;

      if (this.user.startPage == 'leads') {
        this.$router.push({ name: 'leads' });
      } else if (this.user.startPage == 'contacts') {
        this.$router.push({ name: 'companies' });
      } else if (this.user.startPage == 'salesImpact') {
        this.$router.push({ name: 'sales-impact-report' });
      } else {
        this.$router.push({ name: 'user-profile' });
      }
    },
  },
  mounted() {
    window.addEventListener('dragover', (e) => {
      const draggableElementsFullScreen = document.querySelectorAll('.file-upload.fullscreen');
      const popups = document.querySelectorAll('.popup');

      if (draggableElementsFullScreen.length && !popups.length) {
        draggableElementsFullScreen[0].classList.add('dragging');
        e.preventDefault();
      } else {
        e.preventDefault();
      }
    });

    window.addEventListener('dragleave', (e) => {
      const draggableElements = document.querySelectorAll('.file-upload.fullscreen');
      const popups = document.querySelectorAll('.popup');
      if (!draggableElements.length || popups.length) {
        e.preventDefault();
      }
    });

    window.addEventListener('drop', (e) => {
      const draggableElements = document.querySelectorAll('.file-upload.fullscreen');
      const popups = document.querySelectorAll('.popup');
      if (!draggableElements.length || popups.length) {
        e.preventDefault();
      }
    });
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap');

* {
  font-family: 'Inter', sans-serif;
}

body {
  margin: 0;
  padding: 0;
}

#app {
  background: $appBackgroundColor;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

select {
  appearance: none;
  background: white;
  background-image: url('./assets/dropdown-icon.svg');
  background-repeat: no-repeat;
  background-position: right 7px center;
  background-size: 10px;
}

img {
  -webkit-user-drag: none;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

@media print {
  #app {
    overflow: visible;
    height: auto;
  }

  ::-webkit-scrollbar {
    display: none;
  }
}
</style>
